import AddCircleIcon from "@mui/icons-material/AddCircle";
import UndoIcon from "@mui/icons-material/Undo";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import usePermission from "CustomHooks/usePermission";
import { app_modules } from "helpers/apps_modules_list";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportEnquiriesCSV,
  fetchEnquiries,
  updateEnquiryStage,
} from "slices/enquiry/thunk";
import Swal from "sweetalert2";
import { showConfirm, showCustomSwal } from "utils/swalHelper";

interface QuotationRequestProps {
  pageIndex: number;
  pageSize: number;
  sorting: any[];
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  sortField: string;
  sortDirection: "" | "asc" | "desc" | undefined;
  setPageIndex: (value: number) => void;
  setPageSize: (value: number) => void;
  setSorting: (value: any) => void;
  setColumnFilters: (value: any) => void;
  setFilterValue: (value: any[]) => void;
  setSearch: (value: string) => void;
  setSearchInput: (value: string) => void;
  onHeaderReady?: (handlers: {
    searchInput: string;
    setSearchInput: (value: string) => void;
    handleExport: () => void;
  }) => void;
}
const QuotationRequest: React.FC<QuotationRequestProps> = ({
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  sortField,
  sortDirection,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setSearch,
  setSearchInput,
  onHeaderReady,
}) => {
  document.title = "Quotation Request List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Redux selector for enquiry state
  const selectEnquiries = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      enquiries: enquiry.enquiryList,
      rowCount: enquiry.rowCount,
      loading: enquiry.loading,
      error: enquiry.error,
    })
  );

  const { enquiries, rowCount, loading, error } = useSelector(selectEnquiries);

  // Use custom hook to fetch and access permissions
  const { canCreate, canUpdate } = usePermission(
    app_modules?.order?.app_name,
    app_modules?.order?.modules?.quotation
  );


  // Memoize filters to prevent infinite loops
  const allFilters = useMemo(() => {
    const stageFilter = {
      filterField: "stage",
      filterValue: "Request For Quotation",
    };

    // Check if stage filter already exists
    const hasStageFilter = filterValue.some(
      (f: any) => f.filterField === "stage" && f.filterValue === "Request For Quotation"
    );

    return hasStageFilter ? filterValue : [...filterValue, stageFilter];
  }, [filterValue]);

  // Fetch enquiries with stage filter "request for quotation"
  useEffect(() => {
    dispatch(
      fetchEnquiries({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: allFilters,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageIndex, pageSize, sorting, search, allFilters]);

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  // Use data from backend (sr_no is already provided by backend)
  const data = useMemo(() => {
    return enquiries;
  }, [enquiries]);

  // Handle export
  const handleExport = useCallback(async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Add stage filter to filterValue (only if not already present)
      const stageFilter = {
        filterField: "stage",
        filterValue: "Request For Quotation",
      };

      // Check if stage filter already exists
      const hasStageFilter = filterValue.some(
        (f: any) => f.filterField === "stage" && f.filterValue === "Request For Quotation"
      );

      const allFilters = hasStageFilter ? filterValue : [...filterValue, stageFilter];
      console.log(" search : ", search);
      const { blob, filename } = await exportEnquiriesCSV({
        search,
        filters: allFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Quotation requests exported successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  }, [search, filterValue]);

  // Expose handlers to parent
  useEffect(() => {
    if (onHeaderReady) {
      onHeaderReady({
        searchInput,
        setSearchInput,
        handleExport,
      });
    }
  }, [searchInput, onHeaderReady, handleExport]);

  // Format date as DD/MM/YY
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      return moment(dateString).format("DD/MM/YY");
    } catch {
      return dateString;
    }
  };

  // Handle create quotation from enquiry
  const handleCreateQuotation = useCallback(
    (enquiry: any) => {
      const enquiryId = enquiry.enquiry_id || enquiry.id;
      // Navigate to create quotation page with enquiry ID
      navigate(`/quotation/create/${enquiryId}`);
    },
    [navigate]
  );

  // Handle revert enquiry stage from "request for quotation" to "pending"
  const handleRevertStage = useCallback(
    async (enquiryId: string) => {
      try {
        await dispatch(
          updateEnquiryStage({ enquiryId, stage: "Pending" })
        ).unwrap();
        toast.success("Enquiry stage reverted to pending successfully");

        // Refresh the list
        const stageFilter = {
          filterField: "stage",
          filterValue: "Request For Quotation",
        };
        const allFilters = [...filterValue, stageFilter];

        dispatch(
          fetchEnquiries({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: allFilters,
          })
        );
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.error ||
          err?.message ||
          "Failed to revert enquiry stage";
        toast.error(errorMessage);
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
    ]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
       {
        icon: AddCircleIcon,
        label: "Create",
        permission: canCreate,
        onClick: (enquiry: any) => {
          handleCreateQuotation(enquiry);
        },
        color: "themeColor",
      },
      {
        icon: UndoIcon,
        label: "Revert",
        permission: canUpdate,
        onClick: async (enquiry: any) => {
          const result = await showConfirm(
            "Are you sure you want to revert this enquiry stage to pending ?",
            "Revert Enquiry Stage?",
            "Yes",
            "No"
          );
            if (result.isConfirmed) {
              handleRevertStage(enquiry.enquiry_id || enquiry.id);
            }
   
        },
        color: "error",
      },

     
    ];
    return items;
  }, [handleRevertStage, handleCreateQuotation, canCreate, canUpdate]);


  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasAnyMenuItemPermission = menuItems.some(
      (item) => item.permission === true
    );

    if (!hasAnyMenuItemPermission) return [];

    return [
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          stickyRight: true,
        },
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems]);
  


  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.sr_no || "-",
      },
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/enquiry/dropdown/enquiry_number/?stage=Request For Quotation",
        cell: ({ row }: { row: any }) => row.original.enquiry_number || "-",
      },
      {
        header: "Enquiry Date",
        accessorKey: "enquiry_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return new Date(row.original.enquiry_date)
            .toISOString()
            .split("T")[0];
        },
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/enquiry/dropdown/customer_name/?stage=Request For Quotation",
        cell: ({ row }: { row: any }) => {
          return row.original.customer_details?.customer_name || "-";
        },
      },
      {
        header: "Phone Number",
        accessorKey: "contact_person_phone_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.customer_details?.contact_person_phone_no || "-";
        },
      },
      {
        header: "Site Visit",
        accessorKey: "site_visit_required",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.site_visit_required ? "Yes" : "No";
        },
      },
      {
        header: "Staff Name",
        accessorKey: "staff_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/enquiry/dropdown/staff_name/?stage=Request For Quotation",
        cell: ({ row }: { row: any }) => {
          // Get staff name from site_visit object (denormalized field)
          // staff_details is an object, but staff_name is the denormalized string field
          return row.original.site_visit?.staff_name || "-";
        },
      },
      {
        header: "Site Visit Date",
        accessorKey: "site_visit_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl:
          "/enquiry/dropdown/site_visit_date/?stage=Request For Quotation",
        cell: ({ row }: { row: any }) => {
          // return formatDate(row.original.site_visit?.site_visit_date) || "-";
          const raw = row.original?.site_visit?.site_visit_date;
          if (!raw) return "-";

          const d = new Date(raw);
          return d.toISOString().split("T")[0];
        },
      },
      ...DynamicAction,
    ],
    [menuItems, DynamicAction]
  );

  return (
    <Row id="quotationRequestList">
      <Col lg={12}>
        <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading} error={error}>
              <TableContainer
                columns={columns}
                data={data}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search Quotation Requests..."
                manualSorting={true}
                colValue={setFilterValue}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default QuotationRequest;
